import { useState, useEffect, useRef } from 'react';
import { getCountryData } from 'services/ServiceCOUNTRYS';

/**
 * Custom hook for managing phone input with country selection and validation.
 *
 * @returns {Object} Hook state and handlers:
 *   @property {boolean} isDropdownOpen - Whether the country dropdown is open.
 *   @property {Function} setIsDropdownOpen - Setter for isDropdownOpen.
 *   @property {string} searchTerm - Current search term for filtering countries.
 *   @property {Function} setSearchTerm - Setter for searchTerm.
 *   @property {Array<Object>} countries - List of all country data.
 *   @property {Array<Object>} filteredCountries - List of countries filtered by searchTerm.
 *   @property {boolean} isLoading - Whether country data is loading.
 *   @property {React.RefObject} dropdownRef - Ref for the dropdown element.
 *   @property {React.RefObject} inputRef - Ref for the input element.
 *   @property {Function} validatePhoneNumber - Validates a phone number string.
 */

export const usePhoneInput = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoading(true);
      try {
        const data = await getCountryData();
        setCountries(data);
        setFilteredCountries(data);
      } catch (error) {
        console.error(
          '[PhoneInput] Falha ao carregar dados dos países:',
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (searchTerm && countries.length > 0) {
      const filtered = countries.filter(
        (country) =>
          country.name
            .toLowerCase()
            .replace('+', '')
            .includes(searchTerm.replace('+', '').toLowerCase()) ||
          country.dialCode
            .replace('+', '')
            .includes(searchTerm.replace('+', ''))
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [searchTerm, countries]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^\d{5,15}$/;
    return phoneRegex.test(phoneNumber);
  };

  return {
    isDropdownOpen,
    setIsDropdownOpen,
    searchTerm,
    setSearchTerm,
    countries,
    filteredCountries,
    isLoading,
    dropdownRef,
    inputRef,
    validatePhoneNumber
  };
};
